/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { Box, styled } from '@mui/material';
import type { i18n, TFunction } from 'i18next';
import React from 'react';
import { CustomIconButton, CustomTooltip } from '@opengeoweb/shared';
import { breakpoints, Delete, Info } from '@opengeoweb/theme';
import { AssignedAreas } from '../AssignedAreas/AssignedAreas';
import type {
  ChangeType,
  DistrictsType,
  LevelDefinitionType,
  MaritimeWarningsDomain,
  MaritimeLevelTiming,
  MaritimeWarningItem,
  MaritimeWarningItemAttributes,
  MaritimeWarningsConfig,
  WindDirectionType,
} from '../types';
import { WarningListDragHandle } from './WarningListDragHandle';
import type { WarningListSelectOption } from './WarningListSelect';
import { WarningListSelect } from './WarningListSelect';
import type { WindForceLevel, WindForceOption } from './WindForceSelect';
import { WindForceSelect } from './WindForceSelect';
import { useWarningsTranslation } from '../../../utils/i18n';
import { validateWindDirectionChange } from '../utils';

const getWindDirectionsFromConfig = (
  directionConfig: WindDirectionType[],
  t: TFunction<'warn', i18n>,
): WarningListSelectOption[] => {
  return directionConfig.map((direction) => {
    return {
      value: direction.value,
      label: t(`maritime-direction-${direction.value}`) || direction.label,
    };
  });
};

const getWindForceFromConfig = (
  forceConfig: Record<
    MaritimeLevelTiming,
    Record<MaritimeWarningsDomain, LevelDefinitionType[]>
  >,
  domain: MaritimeWarningsDomain,
): WindForceOption[] => {
  if (forceConfig.maritimeLevelsSummer) {
    return forceConfig.maritimeLevelsSummer[domain].map((force) => {
      return {
        value: force.windForce,
        level: force.level as WindForceLevel,
      };
    });
  }
  return forceConfig.maritimeLevelsWinter[domain].map((force) => {
    return {
      value: force.windForce,
      level: force.level as WindForceLevel,
    };
  });
};

const getChangeTypeFromConfig = (
  changeConfig: ChangeType[],
  t: TFunction<'warn', i18n>,
): WarningListSelectOption[] => {
  return changeConfig.map((change) => {
    return {
      value: change.key,
      label: t(`maritime-change-${change.key}`) || change.label,
    };
  });
};
export interface WarningListItemProps {
  warning: MaritimeWarningItem;
  areas: DistrictsType[];
  selectedAreaIds: string[];
  isReadOnly: boolean;
  onAddClick: (id: string) => void;
  onRemove: (id: string) => void;
  onChange: (id: string, values: MaritimeWarningItemAttributes) => void;
  onToggleSelectArea: (id: string) => void;
  onSelectMultipleAreas: (ids: string[]) => void;
  onRemoveArea: (id: string) => void;
  shouldShowDistricts?: boolean;
  maritimeConfig: MaritimeWarningsConfig;
  domain: MaritimeWarningsDomain;
}

export const WarningListItem = React.memo(
  ({
    warning,
    areas,
    selectedAreaIds,
    onAddClick,
    onRemove,
    onChange,
    onToggleSelectArea,
    onSelectMultipleAreas,
    onRemoveArea,
    isReadOnly,
    shouldShowDistricts = false,
    maritimeConfig,
    domain,
  }: WarningListItemProps) => {
    const areasById = React.useMemo(() => {
      return new Map(areas.map((area) => [area.districtName, area]));
    }, [areas]);

    const assignedAreas = React.useMemo(() => {
      return warning.assignedAreaIds
        .map((areaId) => areasById.get(areaId))
        .filter((area): area is DistrictsType => area !== undefined);
    }, [warning.assignedAreaIds, areasById]);

    const { t } = useWarningsTranslation();

    const handleChangeFromDirection = React.useCallback(
      (value: string) => {
        onChange(warning.id, {
          windDirectionFrom: value,
        });
      },
      [onChange, warning.id],
    );

    const handleChangeWindForce = React.useCallback(
      (value: string) => {
        onChange(warning.id, {
          windForce: value,
        });
      },
      [onChange, warning],
    );

    const handleChangeWindChangeType = React.useCallback(
      (value: string) => {
        onChange(warning.id, {
          changeType: value,
        });
        if (value === 'NO_CHANGE') {
          onChange(warning.id, {
            windDirectionTo: '',
          });
        }
      },
      [onChange, warning],
    );

    const handleChangeToDirection = React.useCallback(
      (value: string) => {
        onChange(warning.id, {
          windDirectionTo: value,
        });
      },
      [onChange, warning],
    );

    const handleAddClick = React.useCallback(() => {
      onAddClick(warning.id);
    }, [warning.id, onAddClick]);

    const shouldDisableDirTo = warning.changeType === 'NO_CHANGE' || isReadOnly;

    const changeOptions = getChangeTypeFromConfig(maritimeConfig.changeType, t);

    const validationResult = validateWindDirectionChange(
      warning.windDirectionFrom,
      warning.windDirectionTo,
      warning.changeType,
      maritimeConfig,
    );
    const showInfoIcon = validationResult !== true;

    return (
      <StyledItem data-testid="maritime-warning">
        {!isReadOnly && <WarningListDragHandle />}

        <WarningListSelect
          emptyMessage={`(${t('maritime-header-direction')})`}
          gridArea="dirFrom"
          isReadOnly={isReadOnly}
          label={t('maritime-header-direction-from')}
          onChange={handleChangeFromDirection}
          options={getWindDirectionsFromConfig(
            maritimeConfig.windDirectionDefinition,
            t,
          )}
          value={warning.windDirectionFrom}
        />

        <WindForceSelect
          gridArea="windForce"
          isReadOnly={isReadOnly}
          onChange={handleChangeWindForce}
          options={getWindForceFromConfig(
            maritimeConfig.maritimeLevelDefinition,
            domain,
          )}
          value={warning.windForce}
        />

        <AssignedAreas
          assignedAreas={assignedAreas}
          isReadOnly={isReadOnly}
          onAddClick={handleAddClick}
          onDeleteClick={onRemoveArea}
          onSelectMultipleAreas={onSelectMultipleAreas}
          onToggleSelectArea={onToggleSelectArea}
          selectedAreaIds={selectedAreaIds}
          shouldShowDistricts={shouldShowDistricts}
        />

        <WarningListSelect
          emptyMessage={t('maritime-no-change-selected')}
          gridArea="changeType"
          isReadOnly={isReadOnly}
          label={t('maritime-header-change')}
          onChange={handleChangeWindChangeType}
          options={changeOptions}
          validationResult={validationResult}
          value={warning.changeType}
        />

        <WarningListSelect
          gridArea="dirTo"
          isReadOnly={shouldDisableDirTo}
          label={t('maritime-header-direction-to')}
          onChange={handleChangeToDirection}
          options={getWindDirectionsFromConfig(
            maritimeConfig.windDirectionDefinition,
            t,
          )}
          value={warning.windDirectionTo}
          {...(warning.changeType !== 'NO_CHANGE' && {
            emptyMessage: `(${t('maritime-header-direction')})`,
          })}
        />

        {showInfoIcon ? (
          <Box
            sx={{
              gridArea: 'info',
              justifySelf: 'left',
              height: '24px',
            }}
          >
            <CustomTooltip
              title={t(`maritime-wind-change-${validationResult}`)}
            >
              <span>
                <Info
                  sx={{
                    marginTop: 0.5,
                    color: `geowebColors.functional.${validationResult}`,
                  }}
                />
              </span>
            </CustomTooltip>
          </Box>
        ) : null}

        {!isReadOnly && (
          <CustomIconButton
            aria-label={t('maritime-delete-button')}
            onClick={() => onRemove(warning.id)}
            size="small"
            sx={{
              gridArea: 'delete',
              justifySelf: 'end',
              padding: 0,
              marginTop: 0.5,
            }}
            tooltipTitle={t('maritime-delete-warning')}
          >
            <Delete />
          </CustomIconButton>
        )}
      </StyledItem>
    );
  },
);

const StyledItem = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(0.25),
  backgroundColor: theme.palette.geowebColors.cards.cardContainer,
  borderColor: theme.palette.geowebColors.cards.cardContainerBorder,
  borderRadius: theme.shape.borderRadius,
  borderWidth: '1px',
  borderStyle: 'solid',
  display: 'grid',
  alignItems: 'start',
  gap: theme.spacing(0.5),
  paddingInline: theme.spacing(0.25),

  // styling for small windows
  gridTemplateAreas: `
        "drag dirFrom    dirFrom windForce  windForce delete"
        ".    areas      areas   areas      areas     ."
        ".    changeType dirTo   dirTo      info      ." 
    `,
  gridTemplateColumns: '16px minmax(82px, 1fr) 1fr minmax(60px, 1fr) 20px 20px',

  // larger window styling (note that the grid columns are defined in warning-list)
  [`@container maritime (min-width: ${breakpoints.tablet}px)`]: {
    gridTemplateAreas: `
        "drag dirFrom windForce areas changeType dirTo info delete" 
    `,
    gridTemplateColumns: 'var(--grid-columns)',
  },
}));
